import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { FontSizeProvider } from "./contexts/FontSizeContext";
import Navbar from "./components/Navbar";
import Chatbot from "./components/Chatbot";
import LandingPage from "./pages/LandingPage";
import { CourseCatalog, CourseDetail } from "./pages/CourseCatalog";
import PricingPage from "./pages/PricingPage";
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from "./pages/AuthPages";
import StudentDashboard from "./pages/StudentDashboard";
import JobBoard from "./pages/JobBoard";
import CareerAdvisor from "./pages/CareerAdvisor";
import EmployerDashboard from "./pages/EmployerDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import PaymentSuccess from "./pages/PaymentSuccess";
import PartnerPage from "./pages/PartnerPage";
import ProfilePage from "./pages/ProfilePage";
import CoursePlayer from "./pages/CoursePlayer";
import { BlogList, BlogPost } from "./pages/BlogPage";
import EventsPage from "./pages/EventsPage";
import { TutorialList, TutorialView } from "./pages/Tutorials";

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-zinc-400">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function RedirectByRole() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'admin') return <Navigate to="/admin" replace />;
  if (user.role === 'employer') return <Navigate to="/employer" replace />;
  return <Navigate to="/dashboard" replace />;
}

function AppRoutes() {
  const { user } = useAuth();
  
  return (
    <>
      <Navbar />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/courses" element={<CourseCatalog />} />
        <Route path="/courses/:slug" element={<CourseDetail />} />
        <Route path="/tutorials" element={<TutorialList />} />
        <Route path="/tutorials/:slug" element={<TutorialView />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/jobs" element={<JobBoard />} />
        <Route path="/career-advisor" element={<CareerAdvisor />} />
        <Route path="/partners" element={<PartnerPage />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/login" element={user ? <RedirectByRole /> : <LoginPage />} />
        <Route path="/register" element={user ? <RedirectByRole /> : <RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/dashboard" element={<ProtectedRoute allowedRoles={['student', 'admin']}><StudentDashboard /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute allowedRoles={['student', 'admin']}><ProfilePage /></ProtectedRoute>} />
        <Route path="/learn/:slug" element={<ProtectedRoute allowedRoles={['student', 'admin']}><CoursePlayer /></ProtectedRoute>} />
        <Route path="/employer" element={<ProtectedRoute allowedRoles={['employer']}><EmployerDashboard /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />
        <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Chatbot />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <FontSizeProvider>
            <AppRoutes />
          </FontSizeProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
